import {
  Card,
  Badge,
  Button,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useExports,
  useDownloadExport,
  useProcessExport,
  type Export,
} from './useIntegration';

/* ── Helpers ── */

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const statusVariant: Record<string, 'info' | 'success' | 'error' | 'neutral'> = {
  pending: 'info',
  processing: 'info',
  completed: 'success',
  failed: 'error',
};

const statusLabel: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  completed: 'Concluido',
  failed: 'Falhou',
};

/* ── Main Page ── */

export function IntegrationExportsPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: exports, isLoading, error } = useExports();
  const downloadMutation = useDownloadExport();
  const processMutation = useProcessExport();

  const handleProcess = (id: string): void => {
    processMutation.mutate(id, {
      onSuccess: () => {
        addToast('Processamento iniciado', 'success');
      },
      onError: (err) => {
        addToast(
          (err as Error).message ?? 'Erro ao processar exportacao',
          'error',
        );
      },
    });
  };

  const handleDownload = (id: string): void => {
    downloadMutation.mutate(id, {
      onSuccess: () => {
        addToast('Download iniciado', 'success');
      },
      onError: (err) => {
        addToast(
          (err as Error).message ?? 'Erro ao baixar arquivo',
          'error',
        );
      },
    });
  };

  return (
    <div className="integration-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Exportações</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Exportações de dados e seu status.</p>
        </div>
      </div>

      <Card>
        {isLoading ? (
          <div
            style={{
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}
          >
            <LoadingSkeleton count={4} height="48px" />
          </div>
        ) : error ? (
          <div className="state-message error">
            <p>Erro ao carregar exportacoes.</p>
          </div>
        ) : !exports || exports.length === 0 ? (
          <div className="state-message empty">
            <p>Nenhuma exportacao encontrada.</p>
            <p style={{ fontSize: '0.8125rem', color: '#74777f' }}>
              As exportacoes geradas aparecerao aqui.
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <div className="table-header">
              <span className="col-type">Tipo</span>
              <span className="col-format">Formato</span>
              <span className="col-status">Status</span>
              <span className="col-filters">Arquivo</span>
              <span className="col-date">Criado em</span>
              <span className="col-actions">Ações</span>
            </div>
            {exports.map((exp: Export) => (
              <div key={exp.id} className="table-row">
                <span className="col-type">{exp.type}</span>
                <span className="col-format">{exp.format.toUpperCase()}</span>
                <span className="col-status">
                  <Badge variant={statusVariant[exp.status] ?? 'neutral'}>
                    {statusLabel[exp.status] ?? exp.status}
                  </Badge>
                </span>
                <span className="col-filters">
                  {/* Era a coluna "Filtros", que lia um campo inexistente
                      (`filters`, sempre `{}`) e por isso mostrava "-" em toda
                      linha. Nome do arquivo e registros o DTO publica. */}
                  <span className="exp-file" title={exp.fileName}>
                    {exp.fileName}
                  </span>
                  <small className="exp-file__meta">
                    {exp.totalRecords.toLocaleString('pt-BR')} registro
                    {exp.totalRecords === 1 ? '' : 's'}
                    {exp.fileHashSha256 ? ` · sha256 ${exp.fileHashSha256.slice(0, 8)}` : ''}
                  </small>
                </span>
                <span className="col-date">
                  {formatDate(exp.createdAt)}
                  {exp.completedAt ? (
                    <small className="exp-file__meta">
                      concluída em {formatDate(exp.completedAt)}
                    </small>
                  ) : null}
                </span>
                <span className="col-actions">
                  {exp.status === 'pending' || exp.status === 'failed' ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={processMutation.isPending}
                      onClick={() => handleProcess(exp.id)}
                    >
                      {processMutation.isPending ? 'Processando...' : 'Processar'}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={exp.status !== 'completed'}
                    onClick={() => handleDownload(exp.id)}
                  >
                    {exp.status === 'completed' ? 'Download' : 'Indisponivel'}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <style>{`
        .integration-page { padding: 1.5rem; }

        .state-message {
          padding: 2rem;
          text-align: center;
        }
        .state-message p { margin: 0.25rem 0; font-size: 0.875rem; color: #43474e; }
        .state-message.error p { color: #ba1a1a; }

        .table-wrapper {
          font-size: 0.8125rem;
        }
        .table-header,
        .table-row {
          display: grid;
          grid-template-columns: 1fr 80px 110px 1.5fr 130px 220px;
          gap: 1rem;
          padding: 0.75rem 1rem;
          align-items: center;
        }
        .table-header {
          background: #f2f4f6;
          font-weight: 600;
          color: #43474e;
          border-bottom: 1px solid #e0e3e5;
        }
        .table-row {
          border-bottom: 1px solid #eceef0;
          transition: background 0.15s;
        }
        .table-row:hover {
          background: #f2f4f6;
        }
        .col-filters {
          overflow: hidden;
          min-width: 0;
        }
        .exp-file {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 12px;
        }
        .exp-file__meta {
          display: block;
          color: #74777f;
          font-size: 12px;
          white-space: nowrap;
        }
        .col-actions {
          display: flex;
          gap: 0.5rem;
        }
      `}</style>
    </div>
  );
}
